// frontend/src/features/ogdb2/components/BulkAnalysisAutoAskModal.jsx
import React, { useEffect, useMemo, useState } from "react";

const OPS = [
  { value: "gt", label: ">" },
  { value: "gte", label: ">=" },
  { value: "lt", label: "<" },
  { value: "lte", label: "<=" },
  { value: "eq", label: "=" },
  { value: "neq", label: "≠" },
];

const SCENARIOS = [
  { key: "assessor", label: "Assessor (A)" },
  { key: "actual", label: "Actual (Act)" },
  { key: "third_party", label: "3rd Party (3P)" },
];

const ACTIONS = [
  { value: "better_of_compared", label: "Set BA to the more beneficial of the two compared values" },
  { value: "better_of_all", label: "Set BA to the most beneficial of (A, Act, 3P)" },
];

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export default function BulkAnalysisAutoAskModal({
  open,
  onClose,
  levers, // [{ code, label, direction }]
  defaultRules = null,
  onPreview, // async ({ rules, overwriteExistingBA, ignoreZeroCandidates }) => { count, perLever, sample }
  onRun, // async ({ rules, overwriteExistingBA, ignoreZeroCandidates }) => void
  busy = false,
  progress = null, // { done, total }
  lastPreview = null, // { count, perLever, sample }

  // Undo support
  canUndo = false,
  onUndo = null, // async () => void
  undoMeta = null, // { createdAt, count }
}) {
  const leverOptions = useMemo(() => {
    const arr = Array.isArray(levers) ? levers : [];
    return arr
      .filter((x) => x?.code)
      .map((x) => ({ value: x.code, label: x.label || x.code }));
  }, [levers]);

  const [rules, setRules] = useState(() => {
    if (Array.isArray(defaultRules) && defaultRules.length) return defaultRules;
    return [
      {
        id: uid(),
        leverCode: "loe_comparable",
        leftScenario: "actual",
        op: "gt",
        rightScenario: "assessor",
        action: "better_of_compared",
      },
    ];
  });

  const [overwriteExistingBA, setOverwriteExistingBA] = useState(false);

  // NEW: default ON (prevents “missing” 3P comparable start rates from winning)
  const [ignoreZeroCandidates, setIgnoreZeroCandidates] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (Array.isArray(defaultRules) && defaultRules.length) setRules(defaultRules);
  }, [open]); // don't wipe edits mid-open

  function updateRule(id, patch) {
    setRules((prev) =>
      (Array.isArray(prev) ? prev : []).map((r) => (r?.id === id ? { ...r, ...(patch || {}) } : r))
    );
  }

  function addRule() {
    setRules((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: uid(),
        leverCode: "loe_comparable",
        leftScenario: "actual",
        op: "gt",
        rightScenario: "assessor",
        action: "better_of_compared",
      },
    ]);
  }

  function removeRule(id) {
    setRules((prev) => (Array.isArray(prev) ? prev : []).filter((r) => r?.id !== id));
  }

  if (!open) return null;

  const undoLabel =
    undoMeta?.createdAt && undoMeta?.count
      ? `Undo last ask (${undoMeta.count} cells)`
      : "Undo last ask";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-800">BA Auto Ask</div>
            <div className="text-xs text-gray-500 mt-1">
              Applies to <b>rows currently showing</b> on the Analysis screen (after filters + sorting).
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="btn" type="button" onClick={onClose} disabled={busy}>
              Close
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700">
            Rules are evaluated per row. If a rule matches, BA for that lever is set to the “most beneficial” value
            based on the lever’s direction (some are better higher, some better lower).
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={overwriteExistingBA}
                onChange={(e) => setOverwriteExistingBA(e.target.checked)}
                disabled={busy}
              />
              Overwrite existing BA values (otherwise only fills blanks)
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={ignoreZeroCandidates}
                onChange={(e) => setIgnoreZeroCandidates(e.target.checked)}
                disabled={busy}
              />
              Ignore candidate values that are 0 (treat 0 as “missing”)
            </label>
          </div>

          <div className="space-y-3">
            {(Array.isArray(rules) ? rules : []).map((r) => (
              <div key={r.id} className="rounded-2xl border border-gray-200 p-3">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                  <div className="md:col-span-4">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Lever</div>
                    <select
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                      value={r.leverCode || ""}
                      onChange={(e) => updateRule(r.id, { leverCode: e.target.value })}
                      disabled={busy}
                    >
                      {leverOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Left</div>
                    <select
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                      value={r.leftScenario || "actual"}
                      onChange={(e) => updateRule(r.id, { leftScenario: e.target.value })}
                      disabled={busy}
                    >
                      {SCENARIOS.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-1">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Op</div>
                    <select
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                      value={r.op || "gt"}
                      onChange={(e) => updateRule(r.id, { op: e.target.value })}
                      disabled={busy}
                    >
                      {OPS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Right</div>
                    <select
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                      value={r.rightScenario || "assessor"}
                      onChange={(e) => updateRule(r.id, { rightScenario: e.target.value })}
                      disabled={busy}
                    >
                      {SCENARIOS.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-3">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Action</div>
                    <select
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                      value={r.action || "better_of_compared"}
                      onChange={(e) => updateRule(r.id, { action: e.target.value })}
                      disabled={busy}
                    >
                      {ACTIONS.map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">
                    Tip: start with “Comparable LOE: Act &gt; A” and “Oil Price: Act &lt; A”.
                  </div>
                  <button className="btn" type="button" onClick={() => removeRule(r.id)} disabled={busy}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button className="btn" type="button" onClick={addRule} disabled={busy}>
              + Add rule
            </button>
          </div>

          {/* Preview */}
          <div className="rounded-2xl border border-gray-200 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-gray-800">Preview</div>
              <div className="flex items-center gap-2">
                {canUndo && typeof onUndo === "function" ? (
                  <button className="btn" type="button" disabled={busy} onClick={() => onUndo()}>
                    {undoLabel}
                  </button>
                ) : null}

                <button
                  className="btn"
                  type="button"
                  disabled={busy}
                  onClick={() => onPreview({ rules, overwriteExistingBA, ignoreZeroCandidates })}
                >
                  Preview changes
                </button>

                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={busy}
                  onClick={() => onRun({ rules, overwriteExistingBA, ignoreZeroCandidates })}
                >
                  Run BA Auto Ask
                </button>
              </div>
            </div>

            {busy && progress?.total ? (
              <div className="text-xs text-gray-600 mt-2">
                Writing BA values… {progress.done} / {progress.total}
              </div>
            ) : null}

            {lastPreview ? (
              <div className="mt-3 text-sm">
                <div>
                  Planned updates: <b>{lastPreview.count || 0}</b>
                </div>

                {lastPreview.perLever && Object.keys(lastPreview.perLever).length ? (
                  <div className="text-xs text-gray-600 mt-2">
                    {Object.entries(lastPreview.perLever).map(([k, v]) => (
                      <span key={k} className="inline-block mr-3">
                        {k}: <b>{v}</b>
                      </span>
                    ))}
                  </div>
                ) : null}

                {Array.isArray(lastPreview.sample) && lastPreview.sample.length ? (
                  <div className="mt-2 text-xs text-gray-600">
                    <div className="font-semibold text-gray-700 mb-1">Sample</div>
                    <ul className="list-disc ml-5">
                      {lastPreview.sample.slice(0, 6).map((s, i) => (
                        <li key={i}>
                          BAID {s.unitId} · {s.lever} → {s.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="text-xs text-gray-500 mt-2">Click “Preview changes” to see how many cells would update.</div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button className="btn" type="button" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
